import React from 'react';
import { useSelector } from 'react-redux';

import { SHERIFF_GOOGLE_PLAY_TIMELINE } from "sheriffs";
import { PostingInfo } from "api";
import { ClientState } from "state/state";
import { detailedPostingExpandGallery, detailedPostingLoadAttached } from "state/detailedposting/actions";
import { getHomeOwnerName } from "state/home/selectors";
import { MinimalStoryInfo } from "ui/types";
import { useDispatcher } from "ui/hook";
import PostingMenu from "ui/posting/PostingMenu";
import StoryBadges from "ui/story/StoryBadges";
import PostingDate from "ui/posting/PostingDate";
import PostingUpdated from "ui/posting/PostingUpdated";
import PostingVisibility from "ui/posting/PostingVisibility";
import PostingDeleting from "ui/posting/PostingDeleting";
import PostingAvatar from "ui/posting/PostingAvatar";
import PostingSource from "ui/posting/PostingSource";
import PostingOwner from "ui/posting/PostingOwner";
import PostingSheriffVisibility from "ui/posting/PostingSheriffVisibility";
import PostingSubject from "ui/posting/PostingSubject";
import PostingReactions from "ui/posting/PostingReactions";
import PostingSharings from "ui/posting/PostingSharings";
import PostingButtons from "ui/posting/PostingButtons";
import PostingViews from "ui/posting/PostingViews";
import EntryHtml from "ui/entry/EntryHtml";
import EntryGallery from "ui/entry/EntryGallery";
import EntryGalleryExpanded from "ui/entry/EntryGalleryExpanded";
import EntryAttachments from "ui/entry/EntryAttachments";
import EntryLinkPreviews from "ui/entry/EntryLinkPreviews";
import PostingComments from "ui/posting/PostingComments";
import Comments from "ui/comment/Comments";
import { REL_CURRENT } from "util/rel-node-name";
import { getPageHeaderHeight } from "util/ui";

interface Props {
    feedName: string | null;
    story: MinimalStoryInfo | null;
    posting: PostingInfo;
    deleting?: boolean | null;
}

export default function DetailedPosting({feedName, story, posting, deleting}: Props) {
    const loadedAttached = useSelector((state: ClientState) => state.detailedPosting.loadedAttached);
    const expanded = useSelector((state: ClientState) => state.detailedPosting.galleryExpanded);
    const isSheriff = useSelector((state: ClientState) => getHomeOwnerName(state) === SHERIFF_GOOGLE_PLAY_TIMELINE);
    const dispatch = useDispatcher();

    if (deleting) {
        return (
            <div className="posting entry mt-2">
                <PostingDeleting/>
            </div>
        );
    }

    const onExpand = () => {
        dispatch(detailedPostingExpandGallery(true));
        if (!loadedAttached) {
            dispatch(detailedPostingLoadAttached());
        }
        scrollToPostingGallery();
    }

    const onCollapse = () => {
        dispatch(detailedPostingExpandGallery(false));
        scrollToPostingGallery();
    }

    return (
        <main className="posting entry mt-2">
            <PostingMenu posting={posting} feedName={feedName} story={story} detailed/>
            <StoryBadges pinned={story != null && story.pinned} recommended={posting.recommended}/>
            <div className="owner-line">
                <PostingAvatar posting={posting}/>
                <div className="owner-info">
                    <PostingSource posting={posting}/>
                    <PostingOwner posting={posting}/>
                    {isSheriff &&
                        <PostingSheriffVisibility posting={posting}/>
                    }
                    <br/>
                    <PostingDate posting={posting}/>
                    {posting.totalRevisions > 1 &&
                        <PostingUpdated
                            createdAt={posting.createdAt}
                            editedAt={posting.editedAt}
                            publishedAt={story != null ? story.publishedAt : posting.createdAt}
                        />
                    }
                    <PostingVisibility posting={posting}/>
                </div>
            </div>
            <PostingSubject posting={posting} preview={false}/>
            <EntryHtml
                className="content"
                postingId={posting.id}
                html={posting.body.text}
                nodeName={REL_CURRENT}
                media={posting.media}
            />
            <EntryAttachments nodeName={REL_CURRENT} media={posting.media ?? null}/>
            {!expanded &&
                <div id="posting-gallery" className="gallery-collapsed">
                    <EntryGallery
                        postingId={posting.id}
                        nodeName={REL_CURRENT}
                        media={posting.media ?? null}
                        onExpand={onExpand}
                    />
                </div>
            }
            <EntryLinkPreviews
                nodeName={REL_CURRENT}
                linkPreviews={posting.body.linkPreviews}
                noFollow={false}
                media={posting.media ?? null}
            />
            <div className="reactions-line">
                <div className="left-group">
                    <PostingReactions nodeName={REL_CURRENT} postingId={posting.id} reactions={posting.reactions}/>
                    <PostingSharings nodeName={REL_CURRENT} posting={posting}/>
                </div>
                <div className="right-group">
                    <PostingViews viewCount={posting.viewCount}/>
                    <PostingComments postingId={posting.id} totalComments={posting.totalComments}/>
                </div>
            </div>
            <PostingButtons posting={posting} feedName={feedName} story={story} menu/>
            {expanded &&
                <EntryGalleryExpanded
                    postingId={posting.id}
                    nodeName={REL_CURRENT}
                    media={posting.media ?? null}
                    onCollapse={onCollapse}
                />
            }
            {posting.receiverDeletedAt == null &&
                <Comments/>
            }
        </main>
    );
}

function scrollToPostingGallery() {
    setTimeout(() => {
        const y = document.getElementById("posting-gallery")!.getBoundingClientRect().top;
        window.scrollBy(0, y - getPageHeaderHeight());
    });
}
